import { useUpdateAtom } from "jotai/utils";
import { useAtom } from "jotai";
import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { blockYouJustCreatedAtom, pastedTextContentAtom } from "../jotai/atoms";
import { styled } from "../stitches.config";
import {
  AnyBlock,
  TextBlock as TextBlockType,
  JSONTextBlock as JSONTextBlockType,
  Size,
  Point2D,
} from "../types";
import { useEditor, EditorContent, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { DEFAULT_GRID_RESOLUTION, GRID_SEAM } from "../consts";
import Typography from "@tiptap/extension-typography";
import Link from "@tiptap/extension-link";
import linkifyStr from "linkify-string";
import { JSONContent } from "@tiptap/core";
import * as Sentry from "@sentry/nextjs";
import { clampPosition, pixelToGridPosition } from "../utils/gridPosition";
import createBlock from "./createBlock";
import metaKey from "../utils/metaKey";

export function useTextPaste() {
  const setPastedTextContent = useUpdateAtom(pastedTextContentAtom);
  return useCallback(
    (
      dataTransfer: DataTransfer | null,
      upsertBlock: (b: AnyBlock) => void,
      mousePosition: Point2D
    ) => {
      const textContentType = dataTransfer?.types.includes("text/html")
        ? "text/html"
        : dataTransfer?.types.includes("text/plain")
        ? "text/plain"
        : null;
      if (!textContentType) return;
      const content = dataTransfer?.getData(textContentType);
      const width = 18;
      const block = createBlock(
        "text/json",
        clampPosition(
          pixelToGridPosition({
            x: mousePosition.x - (width / 2) * DEFAULT_GRID_RESOLUTION,
            y: mousePosition.y - DEFAULT_GRID_RESOLUTION,
          })
        ),
        {
          width,
          height: 2,
        }
      );
      if (!block) return;
      setPastedTextContent({
        id: block.id,
        value: content,
      });
      upsertBlock({ ...block });
    },
    [setPastedTextContent]
  );
}

export const MigrateTextBlockToJSON = ({
  block,
  updateBlock,
}: {
  block: TextBlockType;
  updateBlock: (block: Partial<AnyBlock>) => void;
}) => {
  const editor = useEditor({
    editable: false,
    extensions: [StarterKit, Link, Typography],
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.isDestroyed) return;

    // previously we were linkifying on render
    // we do it here so we can get it into the markup for the editor
    const linkified = linkifyStr(block.value ?? "");

    // Preserve line breaks
    const breaks = linkified.replace(/\n/g, "<br>");

    // editor can take plain test or html
    editor.commands.setContent(breaks);

    updateBlock({
      type: "text/json",
      value: JSON.stringify(editor.getJSON()),
      backupText: block.value ?? "",
    });
  }, [editor, block.value, updateBlock]);

  return null;
};

const TextAutosizer = ({
  content,
  block,
  updateBlock,
  sizeRef,
}: {
  content: JSONContent;
  block: JSONTextBlockType;
  updateBlock: (block: Partial<AnyBlock>) => void;
  sizeRef: React.MutableRefObject<Size>;
}) => {
  const displaySizeRef = useRef<HTMLDivElement | null>(null);

  const editor = useEditor({
    editable: false,
    extensions: [StarterKit, Link, Typography],
    content: content,
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.isDestroyed) return;

    editor.commands.setContent(content);
  }, [editor, content]);

  useEffect(() => {
    if (!editor) return;
    displaySizeRef.current = editor.view.dom as HTMLDivElement;
  }, [editor, displaySizeRef]);

  useEffect(() => {
    sizeRef.current = block.size;
  }, [block.size]);

  // Autoresize the text block under certain conditions
  const BLOCK_MAX_ROW_HEIGHT_FOR_HORIZONTAL_RESIZE = 3;
  const BLOCK_AUTO_WIDTH_MAX_COLS = 12;
  // Block border is 1px wide but on 2 sides so 1 * 2 - 2
  // TODO get from CSS or computed styles
  const BLOCK_BORDER = 2;
  useEffect(() => {
    if (displaySizeRef.current) {
      const displaySize = displaySizeRef.current;
      const displayHeight = Math.ceil(
        (displaySize.clientHeight + GRID_SEAM + BLOCK_BORDER) /
          DEFAULT_GRID_RESOLUTION
      );
      // Make block taller if needed
      const newSize = { ...sizeRef.current, height: displayHeight };
      if (displayHeight > sizeRef.current.height) {
        updateBlock({ size: newSize });
      }
      // If less than max rows tall check if we want to resize horizontally
      if (sizeRef.current.height < BLOCK_MAX_ROW_HEIGHT_FOR_HORIZONTAL_RESIZE) {
        // +2 is extra buffer space, arrived at by trial and error
        const displayWidth = Math.ceil(
          (displaySize.clientWidth + GRID_SEAM + BLOCK_BORDER + 2) /
            DEFAULT_GRID_RESOLUTION
        );
        // Make block wider if less than autoWidthMax
        if (
          (displayWidth <= BLOCK_AUTO_WIDTH_MAX_COLS &&
            displayWidth > sizeRef.current.width) ||
          displayWidth - sizeRef.current.width > 4
        ) {
          updateBlock({
            size: {
              ...newSize,
              width: Math.min(displayWidth, BLOCK_AUTO_WIDTH_MAX_COLS),
            },
          });
        }
      }
    }
  }, [block.value, updateBlock]);

  return (
    <div
      className="absolute-cover"
      style={{
        pointerEvents: "none",
        minWidth:
          block.size.height < BLOCK_MAX_ROW_HEIGHT_FOR_HORIZONTAL_RESIZE
            ? BLOCK_AUTO_WIDTH_MAX_COLS * DEFAULT_GRID_RESOLUTION -
              GRID_SEAM -
              BLOCK_BORDER
            : "none",
      }}
    >
      <EditorContent
        style={{ visibility: "hidden" }}
        className="styled-markdown-text absolute-cover text-sizer"
        editor={editor}
      ></EditorContent>
    </div>
  );
};

const ReadonlyText = ({ content }: { content: JSONContent }) => {
  const editor = useEditor({
    editable: false,
    // Open on click adds an extra click handler, we turn it off so the link handles it
    extensions: [
      StarterKit,
      Link.configure({ openOnClick: false }),
      Typography,
    ],
    content: content,
  });

  useEffect(() => {
    if (!editor) return;
    if (editor.isDestroyed) return;

    editor.commands.setContent(content);
  }, [editor, content]);

  return (
    <EditorContent
      className="styled-markdown-text"
      editor={editor}
    ></EditorContent>
  );
};

function TextEditor({
  block,
  content,
  updateBlock,
  setIsBeingEdited,
  deleteBlock,
  upsertBlock,
  sizeRef,
}: {
  block: JSONTextBlockType;
  content: JSONContent;
  updateBlock: (block: Partial<AnyBlock>) => void;
  setIsBeingEdited: React.Dispatch<React.SetStateAction<boolean>>;
  deleteBlock: (blockId: string) => void;
  upsertBlock: (block: Partial<AnyBlock>) => void;
  sizeRef: React.MutableRefObject<Size>;
}) {
  const editorRef = useRef<Editor | null>(null);
  const deleteFlagRef = useRef<boolean>(false);
  const [pastedTextContent, setPastedTextContent] = useAtom(
    pastedTextContentAtom
  );
  const setBlockYouJustCreated = useUpdateAtom(blockYouJustCreatedAtom);

  useEffect(() => {
    if (!editorRef.current) return;
    if (editorRef.current.isDestroyed) return;

    // Handle updates from other users while editing
    // Content from other users will be set, it will put our cursor at the end of
    // the content. TODO preserve cursor position (or make this fully collaborative)
    const editor = editorRef.current;
    if (JSON.stringify(editor.getJSON()) !== JSON.stringify(content)) {
      editor.commands.setContent(content, false);
    }
  }, [content]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({ gapcursor: false }),
      Link.configure({ openOnClick: false, linkOnPaste: true }),
      Typography,
    ],
    content: content,
    autofocus: "end",
    onUpdate({ editor }) {
      updateBlock({ value: JSON.stringify(editor.getJSON()) });
    },
    onBlur() {
      setIsBeingEdited(false);
      if (deleteFlagRef.current) {
        deleteBlock(block.id);
      }
    },
    editorProps: {
      // Here tiptap is hooking into prosemirror so you may need to check
      // prosemirror docs for more info https://prosemirror.net/docs
      // Returning false tells prosemirror to handle the event normally
      // (returning true would prevent prosemirror from handling it)
      handleKeyDown: (_, e: KeyboardEvent) => {
        // Do not escape IME control key
        if (e.isComposing) {
          return false;
        }

        switch (e.key) {
          case "Backspace":
            e.stopPropagation();
            if (editorRef.current?.isEmpty) {
              // Deleting the block directly here causes an error
              // instead we set a flag to delete the block on blur
              deleteFlagRef.current = true;
              editorRef.current?.commands.blur();
            }
            return false;
          case "Escape":
            editorRef.current?.commands.blur();
            return false;
          case "Tab":
            // Prevent tabbing out of the editor
            e.preventDefault();
            return false;
          case "Enter":
            if (metaKey(e)) {
              e.preventDefault();
              const newBlock = createBlock(
                "text/json",
                {
                  x: block.position.x,
                  y: block.position.y + sizeRef.current.height,
                },
                {
                  width: sizeRef.current.width,
                  height: 2,
                }
              );
              if (!newBlock) return true;
              upsertBlock(newBlock);
              setBlockYouJustCreated(newBlock.id);
              return true;
            } else {
              return false;
            }
          default:
            return false;
        }
      },
      handlePaste: (_, e) => {
        // Keep paste confined to text block
        e.stopPropagation();
        return false;
      },
    },
  });
  editorRef.current = editor;

  useEffect(() => {
    if (editor && pastedTextContent?.id === block.id) {
      // set timeout necessary to get update to fire properly
      // (determined through trial and error)
      setTimeout(() => {
        // We simulate a paste event so that all pasting rules (including autolink) will apply
        // Adapted from https://discuss.prosemirror.net/t/async-text-paste/1211
        editor
          .chain()
          .insertContent(pastedTextContent.value ?? "")
          .command((props) => {
            props.tr.setMeta("paste", true);
            return true;
          })
          .run();
        editor.commands.blur();
      }, 0);
      setPastedTextContent(null);
    }
  }, [editor, pastedTextContent, setPastedTextContent, block.id]);

  return (
    <EditorContent
      onMouseDown={(event) => {
        // prevent moving/selecting block when it's focused
        event.stopPropagation();
      }}
      onContextMenu={(event) => {
        event.stopPropagation();
      }}
      className="absolute-cover active-text-editor styled-markdown-text"
      data-cursor={"text"}
      editor={editor}
    />
  );
}

// JSONTextBlock is our default text block, replacing an earlier plaintext text block
function JSONTextBlock({
  block,
  updateBlock,
  deleteBlock,
  justCreated,
  upsertBlock,
}: {
  block: JSONTextBlockType;
  justCreated: boolean;
  updateBlock: (block: Partial<AnyBlock>) => void;
  deleteBlock: (blockId: string) => void;
  upsertBlock: (block: Partial<AnyBlock>) => void;
}) {
  const [isBeingEdited, setIsBeingEdited] = useState(false);
  const setBlockYouJustCreated = useUpdateAtom(blockYouJustCreatedAtom);
  const [pastedTextContent] = useAtom(pastedTextContentAtom);
  const sizeRef = useRef<Size>(block.size);

  useEffect(() => {
    if (justCreated) {
      setIsBeingEdited(true);
      setBlockYouJustCreated(undefined);
    }
  }, [justCreated, setBlockYouJustCreated]);

  useEffect(() => {
    if (pastedTextContent?.id === block.id) {
      setIsBeingEdited(true);
    }
  }, [pastedTextContent, block.id, setIsBeingEdited]);

  // Parse JSON
  const content: JSONContent = useMemo(() => {
    const blockValue = block.value ?? "";
    try {
      return JSON.parse(blockValue);
    } catch (e) {
      Sentry.captureException(e);
      return null;
    }
  }, [block.value]);

  if (content === null) {
    block.value = JSON.stringify({ type: "doc", content: [] });
  }

  return (
    <TextStyle
      className="absolute-cover"
      onDoubleClick={() => {
        setIsBeingEdited(true);
      }}
    >
      {isBeingEdited ? (
        <>
          <FocusOutline />
          <TextAutosizer
            content={content}
            block={block}
            updateBlock={updateBlock}
            sizeRef={sizeRef}
          />
          <TextEditor
            block={block}
            content={content}
            updateBlock={updateBlock}
            deleteBlock={deleteBlock}
            setIsBeingEdited={setIsBeingEdited}
            upsertBlock={upsertBlock}
            sizeRef={sizeRef}
          />
        </>
      ) : (
        <ReadonlyText content={content} />
      )}
    </TextStyle>
  );
}

export default JSONTextBlock;

// We use a separate stylesheet for the markdown styling so it can be easily
// tweaked with styles tried out in the CSS block editor
// it uses the styled-markdown-class class and src/styledMarkdownText.css

// The normal focus outline is hidden by the editor wrapper
// So we replace it with our own
const FocusOutline = styled("div", {
  outline: "2px solid #007aff",
  position: "absolute",
  width: "calc(100% - 8px)",
  height: "calc(100% - 8px)",
  left: "4px",
  top: "4px",
});

const TextStyle = styled("div", {
  color: "inherit",
  whiteSpace: "break-spaces",
  wordBreak: "break-word",
  "& a": {
    color: "$turquoiseLink",
  },
});
